// Sugalarm — native push data hook (cycle 28).
//
// Mirrors `usePushSubscriptions`'s shape so the Settings →
// Notifications pane can drop the section in next to the existing Web
// Push one. The hook is safe to call on every surface — on the
// kiosk / browser / installed PWA, `isCapacitorNative()` is false and
// every action no-ops with a clear "not available" error.

import { useCallback, useEffect, useState } from "react";

import * as nativePushApi from "../api/nativePush";
import type {
  NativePushPlatform,
  NativePushTestResponse,
  NativePushToken,
} from "../api/nativePush";
import { isCapacitorNative } from "../lib/capacitor";
import {
  disableNativePush,
  enableNativePush,
  isNativePushEnabledLocally,
} from "../lib/nativePush";
import type { ApiError } from "../lib/types";

export type NativePushPermissionState =
  | "granted"
  | "denied"
  | "default"
  | "unsupported";

export interface UseNativePush {
  /** True when the SPA is running inside the Capacitor native shell. */
  supported: boolean;
  /** True when the operator has enabled native push on this device. */
  locallyEnabled: boolean;
  /** Last permission result we observed from the OS. */
  permission: NativePushPermissionState;
  /** All native tokens registered for the current user. */
  tokens: NativePushToken[];
  apnsEnabled: boolean;
  fcmEnabled: boolean;
  loading: boolean;
  error: ApiError | null;
  refresh: () => Promise<void>;
  enable: (opts: { onAlertId: (id: string) => void }) => Promise<void>;
  disable: () => Promise<void>;
  remove: (id: string) => Promise<void>;
  sendTest: () => Promise<NativePushTestResponse | null>;
}

export function useNativePush(enabledArg: boolean): UseNativePush {
  const supported = isCapacitorNative();
  const [permission, setPermission] = useState<NativePushPermissionState>(
    supported ? "default" : "unsupported",
  );
  const [locallyEnabled, setLocallyEnabled] = useState<boolean>(
    () => supported && isNativePushEnabledLocally(),
  );
  const [tokens, setTokens] = useState<NativePushToken[]>([]);
  const [apnsEnabled, setApnsEnabled] = useState<boolean>(false);
  const [fcmEnabled, setFcmEnabled] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<ApiError | null>(null);

  const refresh = useCallback(async () => {
    if (!enabledArg) return;
    setLoading(true);
    setError(null);
    const r = await nativePushApi.listTokens();
    if (r.ok) {
      setTokens(r.data.tokens);
      setApnsEnabled(r.data.apns_enabled);
      setFcmEnabled(r.data.fcm_enabled);
    } else {
      setError(r.error);
    }
    setLoading(false);
  }, [enabledArg]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const enable = useCallback(
    async (opts: { onAlertId: (id: string) => void }) => {
      setError(null);
      if (!supported) {
        const err: ApiError = {
          kind: "server",
          status: 0,
          message:
            "Native push is only available inside the iOS / Android app.",
        };
        setError(err);
        throw new Error(err.message);
      }
      const status = await enableNativePush(opts);
      if (status === "granted") {
        setPermission("granted");
        setLocallyEnabled(true);
      } else if (status === "denied") {
        setPermission("denied");
        setLocallyEnabled(false);
      } else {
        setPermission("default");
      }
      await refresh();
    },
    [refresh, supported],
  );

  const disable = useCallback(async () => {
    setError(null);
    if (!supported) return;
    await disableNativePush();
    setLocallyEnabled(false);
    await refresh();
  }, [refresh, supported]);

  const remove = useCallback(
    async (id: string) => {
      setError(null);
      const r = await nativePushApi.deleteToken(id);
      if (!r.ok && r.error.status !== 404) {
        setError(r.error);
        throw new Error(r.error.message);
      }
      await refresh();
    },
    [refresh],
  );

  const sendTest = useCallback(async () => {
    setError(null);
    const r = await nativePushApi.sendTest();
    if (!r.ok) {
      setError(r.error);
      return null;
    }
    return r.data;
  }, []);

  return {
    supported,
    locallyEnabled,
    permission,
    tokens,
    apnsEnabled,
    fcmEnabled,
    loading,
    error,
    refresh,
    enable,
    disable,
    remove,
    sendTest,
  };
}

// Re-export the platform type so consumers can `import type` from one
// place rather than tunneling through api/nativePush.
export type { NativePushPlatform };
